import { Component, EventEmitter, Input, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NumberUtils } from '@modeso/aumico-lib-core-fe';
import { I18NextService } from 'angular-i18next';

/**
 * A component that implements the "Shadow Copy Pattern" for editable cells.
 * 
 * It displays either a manual (overridden) value or a system-calculated value,
 * with visual feedback when a value has been manually overridden.
 * 
 * Features:
 * - Yellow border and blue text when overridden
 * - Reset icon to revert to system value
 * - Tooltip showing the system value on hover
 */
@Component({
    selector: 'aumico-overridable-value-input',
    templateUrl: './overridable-value-input.component.html',
    styleUrls: ['./overridable-value-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => OverridableValueInputComponent),
            multi: true,
        },
    ],
})
export class OverridableValueInputComponent implements ControlValueAccessor {
    /**
     * The system-calculated value (shadow value).
     * This is the value calculated by the mapping engine.
     */
    @Input() systemValue: number | null = null;

    /**
     * Format string for displaying numbers in the tooltip
     */
    @Input() numberFormat = '1.2-2';

    /**
     * Locale for number formatting
     */
    @Input() locale = 'de-CH';

    /**
     * Emitted when the manual value changes
     */
    @Output() manualValueChange = new EventEmitter<number | null>();

    /**
     * Emitted when the value is reset to system value
     */
    @Output() resetToSystemValue = new EventEmitter<void>();

    // Internal state
    private _manualValue: number | null = null;
    private _displayValue: number | null = null;

    // ControlValueAccessor callbacks
    private onChange: (value: number | null) => void = () => { };
    private onTouched: () => void = () => { };

    disabled = false;
    showTooltip = false;

    constructor(private i18nextService: I18NextService) { }

    /**
     * Get the translated tooltip label
     */
    get tooltipLabel(): string {
        return this.i18nextService.t('aumico_annual_report_cash_flow_system_value_tooltip') as string || 'System';
    }

    /**
     * Get the translated reset aria label
     */
    get resetAriaLabel(): string {
        return this.i18nextService.t('aumico_annual_report_cash_flow_reset_to_system_value') as string || 'Reset to system value';
    }

    /**
     * Returns true if the current value is manually overridden
     */
    get isOverridden(): boolean {
        return this.isManualOverride(this._manualValue);
    }

    /**
     * Returns the value to display in the input field
     * Priority: Manual value > System value
     */
    get displayValue(): number | null {
        return this._displayValue;
    }

    set displayValue(value: number | null) {
        this._displayValue = value;
    }

    /**
     * Handle input value changes
     */
    onInputChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        const newValue = input.value === '' ? 0 : parseFloat(input.value);

        // Check if the new value differs from the system value
        if (this.isDifferentFromSystemValue(newValue)) {
            this._manualValue = newValue;
            this._displayValue = newValue;
            this.manualValueChange.emit(newValue);
        } else {
            // If the value matches system value, clear the manual override
            this._manualValue = null;
            this._displayValue = this.systemValue;
        }

        this.onChange(this._displayValue);
        this.onTouched();
    }

    /**
     * Reset to the system-calculated value
     */
    resetValue(): void {
        this._manualValue = null;
        this._displayValue = this.systemValue;
        this.onChange(this._displayValue);
        this.resetToSystemValue.emit();
        this.manualValueChange.emit(null);
    }

    /**
     * Show tooltip on mouse enter
     */
    onMouseEnter(): void {
        if (this.isOverridden) {
            this.showTooltip = true;
        }
    }

    /**
     * Hide tooltip on mouse leave
     */
    onMouseLeave(): void {
        this.showTooltip = false;
    }

    /**
     * Handle input value changes (ngModelChange)
     */
    onInputValueChange(value: string | number | null): void {
        const newValue = value === '' || value === null || value === undefined ? 0 : Number(value);

        if (newValue !== null && !NumberUtils.isFinite(newValue)) {
            return;
        }

        if (this.isDifferentFromSystemValue(newValue)) {
            this._manualValue = newValue;
            this._displayValue = newValue;
            this.manualValueChange.emit(newValue);
        } else {
            this._manualValue = null;
            this._displayValue = this.systemValue;
            this.manualValueChange.emit(null);
        }

        this.onChange(this._displayValue);
    }

    markTouched(): void {
        this.onTouched();
    }

    // ControlValueAccessor implementation
    writeValue(value: number | null): void {
        this._displayValue = value;
        if (this.isDifferentFromSystemValue(value)) {
            this._manualValue = value;
        } else {
            this._manualValue = null;
        }
    }

    registerOnChange(fn: (value: number | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    /**
     * Update the component when system value changes
     * This preserves manual overrides while updating the shadow value
     */
    updateSystemValue(newSystemValue: number | null): void {
        this.systemValue = newSystemValue;

        // If not overridden, update the display value to the new system value
        if (!this.isOverridden) {
            this._displayValue = newSystemValue;
            this.onChange(this._displayValue);
        }
    }

    /**
     * Set the manual value directly (used when loading saved data)
     */
    setManualValue(manualValue: number | null): void {
        if (manualValue !== null && manualValue !== undefined) {
            this._manualValue = manualValue;
            this._displayValue = manualValue;
        } else {
            this._manualValue = null;
            this._displayValue = this.systemValue;
        }
        this.onChange(this._displayValue);
    }

    private isDifferentFromSystemValue(value: number | null): boolean {
        if (value === null && this.systemValue === null) {
            return false;
        }
        if (value === null || this.systemValue === null) {
            return true;
        }
        return !NumberUtils.areNumbersEqual(value, this.systemValue);
    }

    private isManualOverride(value: number | null): boolean {
        if (value === null || value === undefined) {
            return false;
        }
        if (this.systemValue === null || this.systemValue === undefined) {
            return true;
        }
        return !NumberUtils.areNumbersEqual(value, this.systemValue);
    }
}
